import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft, MapPin, Calendar, Clock, Users, User, 
  CheckCircle, AlertCircle, CreditCard, Star, Share2, Copy, MessageCircle,
  Smartphone, Facebook
} from 'lucide-react';
import WhatsAppShareButton, { WhatsAppShareSuccess } from '../components/WhatsAppShareButton';
import ShareSessionButton, { ShareSessionSuccess } from '../components/ShareSessionButton';
import FavoriteButton from '../components/FavoriteButton';
import VerifiedBadge, { TrustIndicators } from '../components/VerifiedBadge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default activity images fallback
const defaultActivityImages = {
  yoga: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/1e452f8c4598b8876569e5d022232b19096e8b20700c20fcfb52976365aa16b4.png",
  renforcement: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/0d56e8109c57dea73ff0247cbdfe6bf7daa9936e4867d692c7edcc2f26f978c8.png",
  mobilite: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5361f451fa5ae6ca0e92d87b059d435275e8c54bf903370e85625d6a121a4cf0.png",
  outdoor: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/e710bd7b10eae3ae767193a7a72bef903236a30fabcba0542001a8462535d563.png",
  running: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5262e0975539ef5034f608d090b8afb775d3df23f0787b651dfa3f0e5ee50556.png",
  cardio: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5dc4bfd211de1a8834ce8815afca48579c4f338a4c9d8962f8a62493571867b5.png",
  pilates: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/c890ea7b0d0ea0945befd9b6fc31ea74727a26f0e3134cbac28972cb260da623.png",
  boxe: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/969da96aa4bcb2d93d3dede512e06776827dc9eec58a4e86a5d6ead81b62383c.png",
  danse: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/afe1446a1ae5f2b55cf4dcb64778a73f1e2f15111f92f593454d95b8951636ea.png",
  padel: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/571d1cd80825335f0fa66a750009986c0934a4592d1791c4bf12163eaa53866e.png",
  tennis: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/889b4402287ed32da011aefc961a00573e9ecbe172b8f626ffec360b21b34810.png",
};
const getSessionImage = (s) => s?.image_url || defaultActivityImages[s?.category] || null;

// Invite Friend Block Component - Multi-platform sharing
const InviteFriendBlock = ({ session, t, lang }) => {
  const [copied, setCopied] = useState(false);
  
  const sessionUrl = `${window.location.origin}/session/${session.session_id}`;
  const spotsLeft = session.max_participants - session.current_participants;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  };
  
  const generateMessage = () => {
    return `🏃 ${session.title}\n📅 ${formatDate(session.date)} - ${session.start_time}\n📍 ${session.location}\n💰 ${session.price}€\n\n${t('share.bookYourSpot') || 'Réserve ta place'}: ${sessionUrl}`;
  };
  
  const handleWhatsApp = () => {
    const message = generateMessage();
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  const handleSms = () => {
    const message = `${session.title} - ${formatDate(session.date)} - ${session.location} - ${session.price}€ - ${sessionUrl}`;
    window.location.href = `sms:?body=${encodeURIComponent(message)}`;
  };
  
  const handleMessenger = () => {
    const url = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(sessionUrl)}&app_id=966242223397117&redirect_uri=${encodeURIComponent(window.location.origin)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      toast.success(t('share.copied') || 'Copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy link');
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-5 mt-6" data-testid="invite-friend-block">
      <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
        <Users className="w-5 h-5 text-primary" />
        {t('share.inviteFriend') || 'Inviter un ami'}
      </h4>
      <p className="text-sm text-muted-foreground mb-4">
        {spotsLeft > 0 
          ? (t('share.spotsLeftInvite') || `Il reste ${spotsLeft} places, invite tes amis !`).replace('{{count}}', spotsLeft)
          : (t('whatsapp.inviteDesc') || 'Partage cette séance avec tes amis !')
        }
      </p>
      
      {/* Share buttons grid */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Button 
          onClick={handleWhatsApp}
          className="bg-[#25D366] hover:bg-[#20BD5A] text-white" 
          data-testid="whatsapp-invite-btn"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
        <Button 
          onClick={handleSms}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="sms-invite-btn"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          SMS
        </Button>
        <Button 
          onClick={handleMessenger}
          className="bg-[#0084FF] hover:bg-[#0073E6] text-white"
          data-testid="messenger-invite-btn"
        >
          <Facebook className="w-4 h-4 mr-2" />
          Messenger
        </Button>
        <Button 
          onClick={handleCopyLink}
          variant="outline"
          data-testid="copy-link-btn"
        >
          <Copy className="w-4 h-4 mr-2" />
          {copied ? (t('share.copied') || 'Copié !') : (t('share.copyLink') || 'Copier')}
        </Button>
      </div>
    </div>
  );
};

export default function SessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, lang } = useTranslation();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [alreadyBooked, setAlreadyBooked] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);

  const levelLabels = {
    debutant: t('levels.beginner'),
    intermediaire: t('levels.intermediate'),
    avance: t('levels.advanced'),
    tous: t('levels.all'),
  };

  const levelColors = {
    debutant: 'level-debutant',
    intermediaire: 'level-intermediaire',
    avance: 'level-avance',
    tous: 'level-tous',
  };

  useEffect(() => {
    fetchSession();
    if (isAuthenticated) {
      checkIfBooked();
    }
  }, [sessionId, isAuthenticated]);

  const fetchSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      } else {
        toast.error(t('sessionDetail.toastNotFound'));
        navigate('/explore');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error(t('sessionDetail.toastLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const checkIfBooked = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/bookings`, {
        credentials: 'include',
      });
      if (response.ok) {
        const bookings = await response.json();
        const isBooked = bookings.some(
          b => b.session_id === sessionId && b.status === 'confirmed'
        );
        setAlreadyBooked(isBooked);
      }
    } catch (error) {
      console.error('Error checking booking:', error);
    }
  };

  const handleBook = async () => {
    if (!isAuthenticated) {
      toast.info(t('sessionDetail.toastLoginRequired'));
      navigate('/login', { state: { from: `/session/${sessionId}` } });
      return;
    }

    if (user?.role === 'coach') {
      toast.error(t('sessionDetail.toastCoachCannotBook'));
      return;
    }

    setBooking(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (response.ok) {
        toast.success(t('sessionDetail.toastBooked'));
        setAlreadyBooked(true);
        setShowShareSuccess(true);
        fetchSession();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('sessionDetail.toastBookError'));
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(t('sessionDetail.toastBookError'));
    } finally {
      setBooking(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!session) return null;

  const isFull = session.current_participants >= session.max_participants;
  const spotsLeft = session.max_participants - session.current_participants;
  const isConfirmed = session.status === 'confirmed';

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <span className="font-bold uppercase" data-testid="session-detail-title">{t('sessionDetail.title')}</span>
            <FavoriteButton sessionId={sessionId} size="small" />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Session image banner */}
        {getSessionImage(session) && (
          <div className="relative w-full h-48 sm:h-64 rounded-2xl overflow-hidden mb-6" data-testid="session-detail-image">
            <img 
              src={getSessionImage(session)} 
              alt={session.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12131a]/80 via-transparent to-transparent" />
          </div>
        )}

        <div className="mb-8 animate-fade-in">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`px-3 py-1 rounded text-sm font-medium ${levelColors[session.level]}`}>
              {levelLabels[session.level] || session.level}
            </span>
            <span className="px-3 py-1 rounded text-sm font-medium bg-muted text-muted-foreground">
              {session.category}
            </span>
            {session.indoor ? (
              <span className="px-3 py-1 rounded text-sm font-medium bg-blue-500/20 text-blue-400">{t('sessionDetail.indoor')}</span>
            ) : (
              <span className="px-3 py-1 rounded text-sm font-medium bg-green-500/20 text-green-400">{t('sessionDetail.outdoor')}</span>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{session.title}</h1>
          <p className="text-muted-foreground text-lg">{session.description}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card rounded-lg border border-border p-6 space-y-4">
              <h3 className="font-bold uppercase text-sm text-muted-foreground">{t('sessionDetail.practicalInfo')}</h3>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">{formatDate(session.date)}</div>
                  <div className="text-muted-foreground">{session.start_time} - {session.duration_minutes} {t('sessionDetail.minutes')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">{session.location}</div>
                  {session.latitude && session.longitude && (
                    <a 
                      href={`https://www.google.com/maps?q=${session.latitude},${session.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-sm hover:underline"
                    >
                      {t('sessionDetail.viewOnMap')}
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold">
                    {session.current_participants}/{session.max_participants} {t('sessionDetail.participants')}
                  </div>
                  <div className="text-muted-foreground">
                    {spotsLeft > 0 ? `${spotsLeft} ${t('sessionDetail.spotsLeft')}` : t('sessionDetail.full')}
                  </div>
                  {session.min_participants > 1 && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {t('sessionDetail.minRequired').replace('{{count}}', session.min_participants)}
                    </div>
                  )}
                </div>
              </div>

              {session.equipment_required && (
                <div className="pt-4 border-t border-border">
                  <div className="font-semibold mb-2">{t('sessionDetail.equipment')}</div>
                  <p className="text-muted-foreground">{session.equipment_required}</p>
                </div>
              )}
            </div>

            {session.coach && (
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="font-bold uppercase text-sm text-muted-foreground mb-4">{t('sessionDetail.yourCoach')}</h3>
                
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden relative">
                    {session.coach.picture ? (
                      <img src={session.coach.picture} alt={session.coach.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-muted-foreground" />
                    )}
                    {session.coach.is_verified && (
                      <div className="absolute -bottom-1 -right-1">
                        <VerifiedBadge isVerified={true} size="default" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">{session.coach.name}</span>
                      {session.coach.is_verified && (
                        <VerifiedBadge isVerified={true} size="small" />
                      )}
                    </div>
                    {session.coach.profile?.specialties?.length > 0 && (
                      <div className="text-muted-foreground text-sm">
                        {session.coach.profile.specialties.slice(0, 3).join(' - ')}
                      </div>
                    )}
                    {session.coach.profile?.experience_years > 0 && (
                      <div className="text-sm text-primary mt-1">
                        {session.coach.profile.experience_years} {t('sessionDetail.yearsExperience')}
                      </div>
                    )}
                  </div>
                </div>
                
                {session.coach.is_verified && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <TrustIndicators coach={session.coach} />
                  </div>
                )}

                {session.coach.profile?.bio && (
                  <p className="text-muted-foreground mt-4 text-sm">{session.coach.profile.bio}</p>
                )}
              </div>
            )}

            {/* Invite Friend Block */}
            {session && (
              <InviteFriendBlock session={session} t={t} lang={lang} />
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-6 sticky top-24">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-primary mb-1">{session.price}€</div>
                <div className="text-muted-foreground">{t('sessionDetail.perPerson')}</div>
              </div>

              <div className={`flex items-center justify-center gap-2 mb-6 py-2 rounded ${
                isConfirmed ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'
              }`}>
                {isConfirmed ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{t('sessionDetail.sessionConfirmed')}</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{t('sessionDetail.waitingParticipants')}</span>
                  </>
                )}
              </div>

              {showShareSuccess && session ? (
                <WhatsAppShareSuccess 
                  session={session} 
                  onClose={() => setShowShareSuccess(false)} 
                />
              ) : alreadyBooked ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <div className="font-bold text-green-500 mb-3">{t('sessionDetail.youAreEnrolled')}</div>
                  
                  {spotsLeft > 0 && (
                    <div className="mb-4">
                      <WhatsAppShareButton session={session} />
                    </div>
                  )}
                  
                  <Link to="/dashboard" className="text-primary text-sm hover:underline mt-2 inline-block">
                    {t('sessionDetail.viewBookings')}
                  </Link>
                </div>
              ) : isFull ? (
                <Button disabled className="w-full h-14 text-lg" data-testid="book-button-disabled">
                  {t('sessionDetail.full')}
                </Button>
              ) : (
                <Button 
                  onClick={handleBook} 
                  disabled={booking}
                  className="btn-primary w-full h-14 text-lg"
                  data-testid="book-button"
                >
                  {booking ? (
                    <div className="spinner w-5 h-5 border-2"></div>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      {t('sessionDetail.bookNow')}
                    </>
                  )}
                </Button>
              )}

              {!alreadyBooked && !isFull && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center mb-3">
                    {t('sessionDetail.moreFunTogether')}
                  </p>
                  <WhatsAppShareButton session={session} variant="default" />
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center mt-4">
                {t('sessionDetail.securePayment')}
              </p>

              {!isAuthenticated && !alreadyBooked && (
                <p className="text-xs text-center mt-4">
                  <Link to="/login" className="text-primary hover:underline">
                    {t('common.login')}
                  </Link>
                  {' '}{t('sessionDetail.loginToBook')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
